import React, { useRef } from 'react';
import { ConfigProvider } from 'antd';
import styled, { createGlobalStyle, css } from 'styled-components';
import { StyledLink as TextLink, TableMainText, TableSecondaryText } from 'styles';

import { INLINE_BOTTOM_BAR_TEST_ID } from 'components/BottomBar';
import { CollapseMultiPanel } from 'components/CollapseMultiPanel';
import { DETAIL_ROW_LABEL_CLASS } from 'components/DetailRow/DetailRow';
import ModalFormFooter from 'components/Form/ModalFormFooter';
import {
  Input as IconCheckboxInput,
  Label as IconCheckboxLabel,
  LabelText as IconCheckboxText
} from 'components/IconCheckbox/IconCheckbox.styles';
import { FormItem, InputLabelHolder } from 'components/Layout/Form';
import { ContentCol, InputPaddingWrapper, LabelCol } from 'components/Layout/RenderInGrid';
import { Tag as InfoTag } from 'components/Layout/common';
import {
  BlueBgBox,
  CurrentTierContent,
  DeleteLink,
  FooterForced,
  ForcedLabel,
  ProgressBar,
  ProgressFooter,
  ProgressHeaderDate,
  ProgressLine,
  RangeContainer,
  TierMarker,
  TierTitle,
  UserCurrentTier
} from 'components/LineProgressBar/style';
import StyledLink from 'components/StyledLink';
import StyledOutlineButton from 'components/StyledOutlineButton';
import { StyledTable, TableWithNoWrapHeader } from 'components/StyledTable';
import { Tab as SharedTab } from 'components/Tabs';
import Label from 'components/Text/Label';
import StyledTitle from 'components/Title';
import { PageContent } from 'components/page/PageContent';
import { HeaderContainer } from 'components/page/PageHeader';
import { GrayBox } from 'containers/BDO/customers/BDOCustomerCardEditPage/BDOCustomerCardDetails';
import {
  Band,
  FieldLabel,
  FieldValue,
  Name as CustomerName,
  Pill as CustomerPill
} from 'containers/Customer/CustomerIdentityHeader';

import { DARK_MODAL_CLASS } from './constants';
import { darkTokenDeclarations } from './tokens';
import useModalUnderCard, {
  SIDE_GUTTER,
  UNDER_CARD_BODY_CLASS,
  UNDER_CARD_LEFT_VAR,
  UNDER_CARD_VAR
} from './useModalUnderCard';

/*
  Every rule below is a DESCENDANT of this wrapper, so a shared component (PageHeader is used by
  ~75 pages, StyledTable by ~40) renders dark inside an opted-in page and is untouched everywhere
  else. No shared component file is modified. PLA-8844.

  Not covered, because antd portals them into document.body and DOM inheritance cannot reach them:
  Modal, Popover, Tooltip, message toasts, and the pagination page-size Select. Each needs a
  per-instance getPopupContainer / getContainer pointing back here - see IntelligenceThemeScope,
  which documents the same gap.
*/
/* The frame's own page inset. Customer 360's two tabs pass 24 for the top so their header lands on
   the line Customer Insights draws its own at; every other dark page keeps this. */
export const DARK_SCOPE_TOP_INSET = '44px';

export type DarkPageScopeVariant = 'page' | 'orbit-builder';

/*
  A module builder hosted inside Orbit. Only this variant gets these rules, so the existing dark pages
  keep their look: the host owns the inset, and the builder chrome the pages never draw (stepper,
  radio chips, inline footer, section cards) is painted to the Orbit builder frames.
*/
const orbitBuilderVariant = css`
  min-height: 0;
  padding: 0;
  border-radius: 8px;
  /* One opaque surface: nothing of the page under the modal may show between the builder's bands. */
  background: var(--dk-bg-builder);

  /* Lifted inks: the page palette's muted and placeholder greys sit under 4.5:1 on these panels. */
  --dk-text-muted: #8592ad;
  --dk-text-placeholder: #8592ad;
  --dk-accent-strong: #1a6bd8;

  .ant-form-item,
  .ant-form-item-control-input-content,
  ${InputLabelHolder} {
    color: var(--dk-text-primary);
  }

  ${InputLabelHolder} {
    color: var(--dk-text-secondary);
  }

  ${Label} {
    color: var(--dk-text-primary);
  }

  ::placeholder,
  .ant-select-selection-placeholder,
  .ant-picker-input > input::placeholder {
    color: var(--dk-text-placeholder) !important;
  }

  .ant-steps-item-process .ant-steps-item-icon,
  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
    background: var(--dk-accent-strong) !important;
    border-color: var(--dk-accent-strong) !important;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  strong,
  .ant-typography,
  .ant-descriptions-item-label,
  .ant-descriptions-item-content {
    color: var(--dk-text-primary);
  }

  ${StyledLink},
  ${TextLink} {
    color: var(--dk-link);
  }

  /* Inline black inks and light fills that module markup writes in style attributes. */
  [style*='color: rgb(0, 0, 0)'],
  [style*='color: rgba(0, 0, 0'],
  [style*='color: black'] {
    color: var(--dk-text-primary) !important;
  }

  [style*='color: grey'] {
    color: var(--dk-text-secondary) !important;
  }

  [style*='color: red'] {
    color: var(--dk-danger) !important;
  }

  [style*='background-color: rgb(250, 250, 250)'],
  [style*='background-color: rgb(255, 255, 255)'],
  [style*='background-color: white'],
  [style*='background: rgb(250, 250, 250)'],
  [style*='background: rgb(255, 255, 255)'],
  [style*='background: white'] {
    background-color: var(--dk-surface-raised) !important;
  }

  .ant-tag-has-color[style*='rgb(204, 204, 204)'] {
    border-color: var(--dk-border-strong) !important;
    background-color: var(--dk-surface-hover) !important;
    color: var(--dk-text-primary) !important;
  }

  .ant-form-item-explain:not(.ant-form-item-explain-error),
  .ant-form-item-explain > div:not(.ant-form-item-explain-error),
  .ant-form-item-extra {
    color: var(--dk-text-secondary);
  }

  .ant-form-item-explain-error {
    color: var(--dk-danger);
  }

  ${InputLabelHolder} span {
    color: var(--dk-danger);
  }

  .ant-radio-wrapper-disabled,
  .ant-radio-wrapper-disabled span,
  .ant-checkbox-wrapper-disabled span {
    color: var(--dk-text-muted) !important;
  }

  .ant-table,
  .ant-table-wrapper,
  .ant-table-container,
  .ant-table-placeholder,
  .ant-table-tbody > tr > td,
  .ant-table-thead > tr > th {
    background: transparent;
    color: var(--dk-text-primary);
  }

  .ant-table-footer {
    background: var(--dk-surface-raised);
    color: var(--dk-text-primary);
  }

  .ant-collapse,
  .ant-collapse > .ant-collapse-item,
  .ant-collapse-header,
  .ant-collapse-content {
    border-color: var(--dk-border-subtle) !important;
    background: var(--dk-surface-raised) !important;
    color: var(--dk-text-primary) !important;
  }

  .ant-progress-inner {
    background: var(--dk-border-subtle);
  }

  .ant-progress-bg {
    background-color: var(--dk-success) !important;
  }

  .ant-select-item-option-active:not(.ant-select-item-option-disabled),
  .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background: var(--dk-surface-selected) !important;
    color: var(--dk-text-primary);
  }

  [data-testid='voucher-code-setup'] .ant-card-body > div {
    background: var(--dk-surface-raised);
  }

  /* The page palette's hover violet leaves white labels at 4.49:1. */
  .ant-btn-primary:not([disabled]):hover,
  .ant-btn-primary:not([disabled]):focus {
    border-color: #5a51ec !important;
    background: #5a51ec !important;
  }

  .ant-radio-inner {
    border-color: var(--dk-border-strong);
    background: var(--dk-surface-raised);
  }

  .ant-select-multiple .ant-select-selection-item {
    border-color: var(--dk-border-strong);
    background: var(--dk-surface-hover);
    color: var(--dk-text-primary);
  }

  .ant-select-tree,
  .ant-select-tree-list {
    background: transparent;
    color: var(--dk-text-primary);
  }

  .ant-select-tree .ant-select-tree-node-content-wrapper:hover,
  .ant-select-tree .ant-select-tree-node-selected {
    background: var(--dk-surface-selected) !important;
  }

  .ant-alert-info {
    border-color: rgba(38, 138, 255, 0.4);
    background: rgba(38, 138, 255, 0.12);
  }

  .ant-alert-message,
  .ant-alert-description {
    color: var(--dk-text-primary);
  }

  .ant-picker-clear {
    background: var(--dk-surface-raised);
    color: var(--dk-text-secondary);
  }

  .ant-picker-dropdown .ant-picker-footer .ant-picker-today-btn,
  .ant-picker-dropdown .ant-picker-footer .ant-picker-now-btn {
    color: var(--dk-link) !important;
  }

  .sun-editor .se-navigation,
  .sun-editor .se-wrapper-inner-length {
    color: var(--dk-text-secondary);
  }

  [class*='Dropdown__value'] {
    background: var(--dk-surface-card) !important;
    color: var(--dk-text-primary) !important;
  }

  /* Trigger and filter tiles: the shared icon checkbox paints a white tile and near-black labels. */
  ${IconCheckboxLabel} {
    border-color: var(--dk-border-subtle);
    border-radius: 12px;
    background: var(--dk-surface-raised);

    img {
      filter: brightness(0) invert(0.75);
    }
  }

  ${IconCheckboxText} {
    color: var(--dk-text-secondary);
  }

  ${IconCheckboxInput}:hover + ${IconCheckboxLabel} {
    background: var(--dk-surface-hover);
  }

  ${IconCheckboxInput}:checked + ${IconCheckboxLabel} {
    border-color: var(--dk-link);
    background: rgba(38, 138, 255, 0.14);

    img {
      filter: none;
    }

    ${IconCheckboxText} {
      color: var(--dk-link-hover);
    }
  }

  .ant-card {
    border: 1px solid var(--dk-border-subtle) !important;
    border-radius: 14px !important;
    overflow: hidden;
  }

  .ant-card-head {
    background: var(--dk-surface-raised);
    border-bottom: 1px solid var(--dk-border-subtle);
  }

  .ant-steps-item-title {
    color: var(--dk-text-secondary) !important;
  }

  .ant-steps-item-process .ant-steps-item-title {
    color: var(--dk-link) !important;
  }

  .ant-steps-item-wait .ant-steps-item-icon {
    background: var(--dk-surface-raised);
    border-color: var(--dk-border-subtle);
  }

  .ant-steps-item-wait .ant-steps-icon {
    color: var(--dk-text-muted);
  }

  .ant-steps-item-process .ant-steps-item-icon {
    background: var(--dk-link);
    border-color: var(--dk-link);
  }

  .ant-steps-item-process .ant-steps-icon {
    color: var(--dk-on-accent);
  }

  .ant-steps-item-finish .ant-steps-item-icon {
    background: rgba(30, 185, 128, 0.14);
    border-color: var(--dk-success);
  }

  .ant-steps-item-finish .ant-steps-icon {
    color: var(--dk-success);
  }

  .ant-steps-item-tail::after {
    background-color: var(--dk-border-subtle) !important;
  }

  .ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-tail::after {
    background-color: var(--dk-success) !important;
  }

  .ant-radio-button-wrapper {
    background: var(--dk-surface-raised);
    border-color: var(--dk-border-subtle);
    color: var(--dk-text-secondary);
  }

  .ant-radio-button-wrapper:not(:first-child)::before {
    background-color: var(--dk-border-subtle);
  }

  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
    background: var(--dk-link);
    border-color: var(--dk-link);
    color: var(--dk-on-accent);
  }

  .ant-btn-dangerous {
    background: transparent;
    border-color: var(--dk-danger);
    color: var(--dk-danger);
  }

  .ant-tag[data-state='draft'] {
    background: rgba(100, 112, 154, 0.2) !important;
    border: 0 !important;
    border-radius: 999px;
    color: var(--dk-text-secondary) !important;
  }

  [data-testid='${INLINE_BOTTOM_BAR_TEST_ID}'] {
    margin-top: 18px;
    padding: 9px 56px 9px 9px;
    border-radius: 8px;
    background: var(--dk-surface-card);

    .ant-btn {
      height: 46px;
      border-radius: 10px;
      font-weight: 700;
    }

    .ant-btn-default {
      border-color: var(--dk-border-subtle);
      background: var(--dk-surface-raised);
      color: var(--dk-text-primary);
    }
  }

  /* Module form items force their labels to #000 with a triple-class selector. */
  .ant-form-item-label label,
  .${DETAIL_ROW_LABEL_CLASS} {
    color: var(--dk-text-secondary) !important;
  }

  ${InfoTag}, ${InfoTag} ${Label} {
    border: 1px solid var(--dk-border-strong);
    background: var(--dk-surface-hover);
    color: var(--dk-text-primary);
  }

  /* Review screens: label column, value column and row rules of the shared key/value grid. */
  ${LabelCol}, ${LabelCol} ${Label} {
    color: var(--dk-text-secondary);
  }

  ${ContentCol}, ${ContentCol} ${Label} {
    color: var(--dk-text-primary);
  }

  ${InputPaddingWrapper}:not(:last-of-type) ${ContentCol} {
    border-bottom-color: var(--dk-border-subtle);
  }

  /* Rich text: the read-only preview and both editors (SunEditor, react-rte) paint white boxes. */
  .sun-editor {
    border-color: var(--dk-border-subtle);
    background: var(--dk-surface-raised);
  }

  .sun-editor .se-toolbar,
  .sun-editor .se-resizing-bar {
    outline-color: var(--dk-border-subtle);
    border-color: var(--dk-border-subtle);
    background: var(--dk-surface-raised);
  }

  .sun-editor .se-btn {
    color: var(--dk-text-secondary);
  }

  .sun-editor .se-btn:enabled:hover {
    background: var(--dk-surface-hover);
  }

  .sun-editor-editable,
  .sun-editor .se-wrapper .se-wrapper-inner {
    background: var(--dk-surface-raised) !important;
    color: var(--dk-text-primary) !important;
    font-family: inherit;
  }

  /* The review offsets the preview by -15px to cancel SunEditor's own side padding, so keep it. */
  ${ContentCol} .sun-editor-editable {
    padding: 0 15px !important;
    background: transparent !important;
  }

  [class*='RichTextEditor__root'] {
    border-color: var(--dk-border-subtle);
    background: var(--dk-surface-raised);
    color: var(--dk-text-primary);
  }

  [class*='EditorToolbar__root'] {
    border-bottom-color: var(--dk-border-subtle);
  }

  [class*='RichTextEditor__root'] [class*='Button__root'],
  [class*='RichTextEditor__root'] [class*='EditorToolbar__root'] button,
  [class*='RichTextEditor__root'] select {
    border-color: transparent;
    background: transparent;
    color: var(--dk-text-secondary);
  }

  [class*='RichTextEditor__root'] [class*='Button__root'] > span {
    filter: invert(0.8);
  }
`;

const Scope = styled.div<{ $topInset?: string; $variant?: DarkPageScopeVariant }>`
  ${darkTokenDeclarations}

  position: relative;
  min-height: 100%;
  /* Same page inset the Customer 360 page uses (measured 44/38/44/49), so a customer's two screens
     put their header and content on the same lines instead of 49px apart. Only the top is overridable,
     through the topInset prop, and only Customer 360's Engagement tab does so. */
  padding: ${({ $topInset }) => $topInset ?? DARK_SCOPE_TOP_INSET} 38px 44px 49px;
  background: var(--dk-bg-page);
  color: var(--dk-text-primary);

  /* ---- page chrome ------------------------------------------------------ */
  /*
    Targeted with styled-components component selectors rather than class names: these shared
    components hard-code light colours (PageHeader paints #ffffff, TableSecondaryText #9b9b9b) on
    generated classes. Selecting them here repaints them ONLY inside this wrapper.
  */
  /* Customer detail page: two page-exclusive containers paint themselves light. */
  ${Band} {
    background: var(--dk-surface-card);
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.35);
    color: var(--dk-text-primary);
  }

  /* The identity band paints its name, field labels and values from a near-black #0a0a0a. */
  ${CustomerName},
  ${FieldValue} {
    color: var(--dk-text-primary);
  }

  ${FieldLabel} {
    color: var(--dk-text-secondary);
  }

  ${CustomerPill} {
    color: var(--dk-text-primary);
    border-color: var(--dk-border-strong);
  }

  /*
    The REWARDS / LOYALTY PROGRAM / CAMPAIGNS ... strip is the shared components/Tabs, whose
    inactive tab is a white box. Repainted only inside this wrapper, so the ~dozens of other pages
    using it are untouched.
  */
  /*
    The six customer tabs are outlined pills, nodeId 5290:16954-16959: 34 high, radius 8, on the
    raised surface. Active is a DOUBLED WHITE BORDER plus a white label on the same fill - not a
    fill change and not an underline.
  */
  ${SharedTab} {
    height: 34px;
    display: inline-flex;
    align-items: center;
    padding: 0 12px;
    border-radius: 8px;
    background: var(--dk-surface-raised) !important;
    font-size: 12px;
    font-weight: 700;
    line-height: 15px;
    letter-spacing: 0.24px;
  }

  /* The shared Tabs butts its pills together with margin-left:-1px for a joined outline. The design
     draws six separate pills on a uniform 30px gap. */
  ${SharedTab} + ${SharedTab} {
    margin-left: 30px;
  }

  ${SharedTab}[data-active='false'] {
    border: 1px solid var(--dk-border-subtle) !important;
    color: var(--dk-text-secondary) !important;
  }

  ${SharedTab}[data-active='true'] {
    border: 2px solid var(--dk-on-accent) !important;
    color: var(--dk-on-accent) !important;
  }

  ${GrayBox} {
    background-color: var(--dk-surface-raised);
    color: var(--dk-text-primary);
  }

  ${StyledTitle} {
    color: var(--dk-text-primary);
  }

  ${HeaderContainer} {
    background: var(--dk-bg-page);
    border-bottom: 1px solid var(--dk-border-subtle);
    color: var(--dk-text-primary);
    /* The shared header pads 32 on top of the scope's own 44 inset, which reads as a gap rather
       than a header. The border below is this scope's; the light theme shadow under it is not. */
    padding-top: 0;
    box-shadow: none;
  }

  /* PageHeader sets marginTop inline on its tab bar, so only !important reaches it. 16 between the
     title and the strip is more air than the rest of the header keeps. */
  ${HeaderContainer} .ant-tabs-nav {
    margin-top: 8px !important;
  }

  ${TableMainText} {
    color: var(--dk-text-primary);
  }

  ${TableSecondaryText} {
    color: var(--dk-text-secondary);
  }

  .text-muted {
    color: var(--dk-text-muted);
  }

  .ant-page-header,
  .ant-page-header-heading-title,
  h1,
  h2,
  h3,
  h4 {
    color: var(--dk-text-primary);
  }

  /* ---- card ------------------------------------------------------------- */
  /* Card 5290:16869: radius 18, no border and no shadow at all. */
  .ant-card {
    background: var(--dk-surface-card);
    border: 0 !important;
    border-radius: 18px !important;
    box-shadow: none !important;
    color: var(--dk-text-primary);
  }

  .ant-card-body {
    padding-top: 16px;
  }

  .ant-card-head {
    background: transparent;
    border-bottom-color: var(--dk-border-subtle);
    color: var(--dk-text-primary);
  }

  /* ---- table ------------------------------------------------------------
     The design draws the table CHROME-LESS (nodeId 5290:16894): no header fill, no row fill, no
     zebra, and no hover or selected row anywhere in the frames. The only chrome is a 1px bottom
     rule per cell. Header row 34 high with 10 padding, body row 59 with T14 R10 B14 L10.
     StyledTable forces a white header and a #4a4a4a cell ink with !important, so these match that
     weight. Hover and selected feedback are removed by decision, extrapolated to every dark table. */
  ${StyledTable},
  ${TableWithNoWrapHeader} {
    &.ant-table-wrapper td {
      color: var(--dk-text-primary) !important;

      a {
        color: var(--dk-link) !important;
      }
    }

    .ant-table-thead th {
      background: transparent !important;
      color: var(--dk-text-secondary) !important;
      border-bottom: 1px solid var(--dk-border-subtle) !important;
    }
  }

  .ant-table {
    background: transparent;
    color: var(--dk-text-primary);
  }

  .ant-table-thead > tr > th {
    height: 34px;
    padding: 10px;
    background: transparent !important;
    border-bottom: 1px solid var(--dk-border-subtle) !important;
    color: var(--dk-text-secondary);
    font-size: 11px;
    font-weight: 700;
  }

  .ant-table-thead > tr > th::before {
    display: none !important;
  }

  .ant-table-tbody > tr > td {
    height: 59px;
    padding: 14px 10px;
    background: transparent !important;
    border-bottom: 1px solid var(--dk-border-subtle) !important;
  }

  /* No hover and no selected row are drawn, so both are neutralised rather than recoloured. */
  .ant-table-tbody > tr.ant-table-row:hover > td,
  .ant-table-tbody > tr > td.ant-table-cell-row-hover,
  .ant-table-tbody > tr.ant-table-row-selected > td,
  .ant-table-tbody > tr.ant-table-row-selected:hover > td {
    background: transparent !important;
    color: var(--dk-text-primary) !important;
  }

  /*
    AuditLog renders antd's raw Table rather than StyledTable, so its expandable rows keep antd's
    default expand button: a white 17px box with a #f0f0f0 border, whose plus strokes are ::before
    and ::after painted in currentColor. This scope already sets that near-white, so on the dark
    table the control reads as a white square with an invisible plus inside it.
  */
  .ant-table-row-expand-icon {
    background: transparent;
    border-color: var(--dk-border-subtle);
    color: var(--dk-text-secondary);

    &:hover,
    &:focus {
      border-color: var(--dk-link);
      color: var(--dk-text-primary);
    }
  }

  .ant-checkbox-inner {
    background: var(--dk-surface-raised);
    border-color: var(--dk-border-subtle);
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background: var(--dk-accent);
    border-color: var(--dk-accent);
  }

  .ant-table-placeholder,
  .ant-table-placeholder:hover > td {
    background: transparent;
  }

  .ant-empty-description {
    color: var(--dk-text-muted);
  }

  .ant-table-column-sorter {
    color: var(--dk-text-muted);
  }

  /* Pagination buttons: 30x30, radius 6, on the darker nested surface. */
  .ant-pagination-item,
  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    min-width: 30px;
    height: 30px;
    line-height: 28px;
    background: var(--dk-surface-raised) !important;
    border: 1px solid var(--dk-border-subtle) !important;
    border-radius: 6px !important;
  }

  .ant-pagination-item a {
    color: var(--dk-text-secondary);
  }

  .ant-pagination-item-active {
    border-color: var(--dk-link) !important;
  }

  .ant-pagination-item-active a {
    color: var(--dk-link);
  }

  /* ---- tabs ------------------------------------------------------------- */
  .ant-tabs {
    color: var(--dk-text-primary);
  }

  .ant-tabs-tab {
    color: var(--dk-text-secondary);
  }

  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: var(--dk-text-primary);
  }

  .ant-tabs-top > .ant-tabs-nav::before {
    border-bottom-color: var(--dk-border-subtle);
  }

  /*
    Search row, nodeId 5290:16870: a 420x37 search box and a 120x36 primary button, space-between.
    There is no multi-control filter row on this page.
  */
  .ant-input-affix-wrapper,
  .ant-input-search .ant-input-affix-wrapper,
  .ant-input {
    min-height: 37px;
    padding: 10px 14px;
    background: var(--dk-surface-raised);
    border: 1px solid var(--dk-border-subtle);
    border-radius: 8px;
    color: var(--dk-text-primary);
    font-size: 12.5px;
  }

  /*
    The design draws ONE 420x37 field, nodeId 5290:16871, with the magnifier inside the placeholder
    string rather than as a node. antd's Input.Search instead renders a group: a left-rounded field
    plus a detached square button in a different fill. The button is dropped - SearchBar debounces
    onChange, so typing already searches and the button was a second way to do the same thing.
  */
  .ant-input-search {
    max-width: 420px;
    height: 37px;
  }

  /*
    The search's own wrapper is flex: 1 1 0 with width 100%, so it keeps growing to the row's share
    while the field inside stops at 420 - the slack it holds is dead space that pushed the campaign
    dropdown 154 away. Capping the wrapper as well puts the two controls back beside each other.
  */
  div:has(> .ant-input-search) {
    flex: 0 0 420px;
    width: 420px;
    max-width: 420px;
  }

  /* antd lays the group out as a table, so with the addon cell hidden the field still measured to
     the old two-cell width. Flex lets the one remaining cell take the row. */
  .ant-input-search .ant-input-group {
    display: flex;
    width: 100%;
    height: 37px;
  }

  .ant-input-search .ant-input-wrapper > *:first-child {
    flex: 1;
  }

  .ant-input-search .ant-input-group-addon,
  .ant-input-search-button {
    display: none !important;
  }

  /*
    The affix-wrapper IS the field. Styling the inner input as a field too drew a second rounded box
    inside the first, in the same fill and offset by the prefix icon - which read as two search bars.
    The input is therefore bare, and only the wrapper carries surface, border and radius.
  */
  .ant-input-search .ant-input-affix-wrapper {
    border-radius: 8px !important;
  }

  .ant-input-search .ant-input-affix-wrapper > input.ant-input,
  .ant-input-affix-wrapper > input.ant-input {
    width: 100%;
    min-height: auto;
    height: auto;
    padding: 0 !important;
    background: transparent !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }

  .ant-input::placeholder,
  .ant-input-affix-wrapper input::placeholder {
    color: var(--dk-text-secondary);
  }

  .ant-btn-primary {
    height: 36px;
    padding: 0 16px;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 700;
  }

  /*
    Card status tabs (Issued / Pending ...), nodeId 5290:16875: a 2px WHITE bottom border and a
    weight change on the active tab - no pill, no fill. Distinct from the six page tabs above, which
    are outlined pills, and from the in-modal transaction tabs, which use the link ink instead.
  */
  .ant-tabs-tab {
    padding: 0 0 11px;
    font-size: 12.5px;
    font-weight: 400;
    color: var(--dk-text-secondary);
  }

  /*
    Underlined text tabs use the link ink. The same control is drawn #FFFFFF on 5290:16826 and
    #268AFF on 5290:17014; the blue carries 14 uses across 7 roles against white appearing once, so
    white is the outlier. The six page pills above keep their white border - different component.
  */
  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: var(--dk-link);
    font-weight: 700;
  }

  .ant-tabs-ink-bar {
    height: 2px !important;
    background: var(--dk-link) !important;
  }

  /*
    NOT zero: removing the bottom margin lets the table header ride up into the status tabs. The
    top margin is the gap under the search box - the tab padding below zeroes what antd normally
    supplies. PageHeader sets its own marginTop inline, so only the in-card strip takes this.
  */
  .ant-tabs-top > .ant-tabs-nav {
    margin-top: 12px;
    margin-bottom: 16px;
  }

  .ant-tabs-top > .ant-tabs-nav::before {
    border-bottom-color: var(--dk-border-subtle);
  }

  /* ---- inputs ----------------------------------------------------------- */
  /*
    CARD tabs, the type="card" variant (uat's Enrolled / Not Enrolled strip in
    CustomerCampaigns). antd paints the TAB ITSELF for this variant - a light head background plus a
    split border and a top radius - and none of the text-tab rules above touch background or border,
    which is why this strip stayed white on a dark card while its ordinary sibling directly below it
    rendered dark.

    Scoped to .ant-tabs-card so ordinary tab strips elsewhere are untouched, and written with
    antd's own two-selector form (> .ant-tabs-nav and > div > .ant-tabs-nav) so it matches at
    the same specificity antd uses rather than needing !important.

    No Figma node draws this control - it is newer than the 5290 customer generation - so these are
    the scope's own tokens chosen to agree with the sibling strip, NOT values read from a spec.
    PLA-8844.
  */
  .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab,
  .ant-tabs-card > div > .ant-tabs-nav .ant-tabs-tab {
    padding: 6px 16px;
    background: var(--dk-surface-raised);
    border-color: var(--dk-border-subtle);
    color: var(--dk-text-secondary);
  }

  /* The active card tab is the panel it opens onto, so it takes the card fill and drops its bottom
     border to merge with the body below - the same join antd draws in the light theme. */
  .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab-active,
  .ant-tabs-card > div > .ant-tabs-nav .ant-tabs-tab-active {
    background: var(--dk-surface-card);
    border-bottom-color: transparent;
  }

  .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab-active .ant-tabs-tab-btn,
  .ant-tabs-card > div > .ant-tabs-nav .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: var(--dk-text-primary);
  }

  /*
    The strip directly below the card tabs sets borderTop/Right/Left inline with a #D8D8D8
    borderColor (CustomerCampaigns), which draws a light box round it on the dark card. An inline
    style beats a class rule, so this needs !important; the adjacent-sibling hook is used because
    the strip's data-testid is not unique to it. PLA-8844.
  */
  .ant-tabs-card + .ant-tabs {
    border-color: var(--dk-border-subtle) !important;
  }

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-select-selector,
  .ant-picker {
    background: var(--dk-surface-raised);
    border-color: var(--dk-border-subtle);
    color: var(--dk-text-primary);
  }

  .ant-input::placeholder,
  .ant-select-selection-placeholder {
    color: var(--dk-text-muted);
  }

  .ant-input-affix-wrapper .ant-input {
    background: transparent;
  }

  .anticon {
    color: inherit;
  }

  /* ---- buttons ---------------------------------------------------------- */
  .ant-btn-default {
    background: var(--dk-surface-raised);
    border-color: var(--dk-border-strong);
    color: var(--dk-text-primary);
  }

  .ant-btn-default:hover {
    background: var(--dk-surface-hover);
    border-color: var(--dk-accent);
    color: var(--dk-text-primary);
  }

  .ant-btn-link {
    color: var(--dk-link);
  }

  .ant-btn-link:hover {
    color: var(--dk-link-hover);
  }

  .ant-pagination-options .ant-select-selector,
  .ant-select-single .ant-select-selector {
    background: var(--dk-surface-raised) !important;
    border-color: var(--dk-border-subtle) !important;
    color: var(--dk-text-primary) !important;
  }

  .ant-select-selection-item,
  .ant-pagination-options .ant-select-selection-item {
    color: var(--dk-text-primary) !important;
  }

  .ant-select-arrow,
  .ant-select-suffix {
    color: var(--dk-text-secondary);
  }

  .anticon-search {
    color: var(--dk-text-secondary);
  }

  /*
    StatusTag paints these states a near-white grey and antd forces white text on top, so the chip
    is invisible - white on white. It reads badly on a light page too, but this is scoped rather
    than fixed at source because StatusTag has dozens of consumers.
      draft/processing #b9b9b9 | ended #e5e5e5 | unknown states #cccccc
  */
  .ant-tag[data-state='ended'],
  .ant-tag[data-state='draft'],
  .ant-tag[data-state='processing'] {
    background: var(--dk-surface-hover) !important;
    border: 1px solid var(--dk-border-strong) !important;
    color: var(--dk-text-secondary) !important;
  }

  /*
    The remaining states keep StatusTag's light-mode fill, which antd writes inline as a
    background-color, so only !important reaches it. Drawn here as a tinted chip - dark
    ground, semantic border and text - which keeps success and failure apart without
    putting a saturated slab on the dark table. PLA-8844.
  */
  .ant-tag[data-state='completed'],
  .ant-tag[data-state='complete'],
  .ant-tag[data-state='success'],
  .ant-tag[data-state='processed'],
  .ant-tag[data-state='issued'],
  .ant-tag[data-state='active'] {
    background: var(--dk-tint-positive-bg) !important;
    border: 1px solid var(--dk-success) !important;
    color: var(--dk-success) !important;
  }

  .ant-tag[data-state='failed'],
  .ant-tag[data-state='fail'],
  .ant-tag[data-state='timeout'],
  .ant-tag[data-state='expired'],
  .ant-tag[data-state='inactive'] {
    background: var(--dk-tint-negative-bg) !important;
    border: 1px solid var(--dk-danger) !important;
    color: var(--dk-danger) !important;
  }

  .ant-tag[data-state='pending'],
  .ant-tag[data-state='errors'],
  .ant-tag[data-state='redeemed'] {
    background: var(--dk-surface-hover) !important;
    border: 1px solid var(--dk-warning) !important;
    color: var(--dk-warning) !important;
  }

  .ant-tag[data-state='scheduled'],
  .ant-tag[data-state='approved'],
  .ant-tag[data-state='running'] {
    background: var(--dk-surface-hover) !important;
    border: 1px solid var(--dk-link) !important;
    color: var(--dk-link-hover) !important;
  }

  /*
    Filter labels on the Event Logs / Transactions / Campaigns tabs are antd Typography.Text, which
    defaults to rgba(0,0,0,.85). Descriptions supply the detail views (Tries ID, Event Name, the
    transaction detail rows) and draw their own grid borders.
  */
  .ant-typography,
  .ant-typography h1,
  .ant-typography h2,
  .ant-typography h3,
  .ant-typography h4,
  .ant-typography p,
  span.ant-typography {
    color: var(--dk-text-primary);
  }

  .ant-typography-secondary {
    color: var(--dk-text-secondary);
  }

  .ant-descriptions-view,
  .ant-descriptions-view table,
  .ant-descriptions-row,
  .ant-descriptions-row > th,
  .ant-descriptions-row > td {
    border-color: var(--dk-border-subtle) !important;
  }

  .ant-descriptions-bordered .ant-descriptions-item-label {
    background: var(--dk-surface-raised) !important;
  }

  /* Bordered tables draw a grid of their own, separate from the row rules already set above. */
  .ant-table-bordered > .ant-table-container,
  .ant-table-bordered > .ant-table-container > .ant-table-content > table,
  .ant-table-bordered > .ant-table-container > .ant-table-content > table > thead > tr > th,
  .ant-table-bordered > .ant-table-container > .ant-table-content > table > tbody > tr > td,
  .ant-table-container,
  .ant-table-cell {
    border-color: var(--dk-border-subtle) !important;
  }

  /* Same &&& label override as in modals - forms appear on the page too. */
  ${FormItem} .ant-form-item-label > label,
  ${FormItem} .ant-form-item-label label {
    color: var(--dk-text-secondary) !important;
  }

  /*
    Three inline colours that only a stylesheet rule with !important can reach. The components keep
    their light-mode values; these apply only inside this wrapper.
  */
  .reward-name {
    color: var(--dk-text-primary) !important;
  }

  .reward-id {
    color: var(--dk-text-secondary) !important;
  }

  .detail-back-button {
    color: var(--dk-link) !important;
    border-color: var(--dk-border-subtle) !important;
  }

  /* An uncoloured antd Tag is a near-white chip on a dark table; a coloured StatusTag carries its
     own semantic fill and is left alone. */
  .ant-tag:not([class*='ant-tag-has-color']) {
    background: var(--dk-surface-hover);
    border-color: var(--dk-border-strong);
    color: var(--dk-text-primary);
  }

  /* PageContent sets margin 1rem 1rem 5rem; the scope's own padding already supplies the side
     inset, so keeping both pushed the content 16px right of the header above it. */
  ${PageContent} {
    margin-top: 0;
    margin-left: 0;
    margin-right: 0;
  }

  /* Breadcrumb: antd inks the trail rgba(0,0,0,.45) and the current page rgba(0,0,0,.85). */
  .ant-breadcrumb,
  .ant-breadcrumb a,
  .ant-breadcrumb-separator,
  .ant-breadcrumb li:last-child {
    color: var(--dk-text-secondary);
  }

  .ant-breadcrumb li:last-child,
  .ant-breadcrumb a:hover {
    color: var(--dk-text-primary);
  }

  /* Row-actions trigger: the three-dot glyph inherits the cell ink, which the table rules force. */
  .anticon-more,
  .ant-table-cell .anticon {
    color: var(--dk-text-primary);
  }

  /*
    Activate / Deactivate is a StyledOutlineButton, a styled(Button) whose colours come from props
    and whose own rules outrank a plain .ant-btn selector. Its border colour is semantic (green for
    activate, slate for deactivate) so that is left to the component; only the surface and ink move.
  */
  ${StyledOutlineButton} {
    background: var(--dk-surface-raised) !important;
    color: var(--dk-text-primary) !important;
  }

  ${StyledOutlineButton}:hover,
  ${StyledOutlineButton}:focus {
    background: var(--dk-surface-hover) !important;
    color: var(--dk-text-primary) !important;
  }

  ${StyledOutlineButton} .anticon {
    color: inherit !important;
  }

  /* Every button variant, not just the default one - Activate / Deactivate is an outline button. */
  .ant-btn {
    background: var(--dk-surface-raised);
    border-color: var(--dk-border-strong);
    color: var(--dk-text-primary);
  }

  .ant-btn:hover,
  .ant-btn:focus {
    background: var(--dk-surface-hover);
    border-color: var(--dk-accent);
    color: var(--dk-text-primary);
  }

  .ant-btn-primary,
  .ant-btn-primary:hover,
  .ant-btn-primary:focus {
    background: var(--dk-accent);
    border-color: var(--dk-accent);
    color: #ffffff;
  }

  .ant-btn[disabled],
  .ant-btn[disabled]:hover {
    background: var(--dk-surface-hover);
    border-color: var(--dk-border-subtle);
    color: var(--dk-text-muted);
  }

  /*
    Empty state. antd draws its placeholder as an inline SVG of near-white greys, which glares on a
    dark surface; dimming the whole glyph reads correctly without re-inking each path. The row
    itself must not take the table's white hover either.
  */
  .ant-empty-image svg {
    opacity: 0.35;
  }

  .ant-table-placeholder > td,
  .ant-table-placeholder:hover > td,
  .ant-table-tbody > tr.ant-table-placeholder:hover > td {
    background: transparent !important;
  }

  /* ---- pagination ------------------------------------------------------- */
  /* "Go to [ ] Page" quick jumper, and the page-size box beside it. */
  .ant-pagination-options-quick-jumper {
    color: var(--dk-text-secondary);
  }

  .ant-pagination-options-quick-jumper input {
    background: var(--dk-surface-raised);
    border-color: var(--dk-border-subtle);
    color: var(--dk-text-primary);
  }

  .ant-pagination-item a,
  .ant-pagination-total-text,
  .ant-pagination-item-link {
    color: var(--dk-text-secondary);
  }

  .ant-pagination-item {
    background: transparent;
    border-color: var(--dk-border-subtle);
  }

  .ant-pagination-item-active {
    background: var(--dk-surface-hover);
    border-color: var(--dk-accent);
  }

  .ant-pagination-item-active a {
    color: var(--dk-text-primary);
  }

  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    background: transparent;
    border-color: var(--dk-border-subtle);
  }

  /*
    Date-picker panel. It portals, but ConfigProvider re-homes it inside this wrapper, so these
    rules reach it. antd paints the whole panel container white and inks the cells near-black.
  */
  .ant-picker-dropdown,
  .ant-picker-panel-container,
  .ant-picker-panel,
  .ant-picker-date-panel,
  .ant-picker-month-panel,
  .ant-picker-year-panel,
  .ant-picker-decade-panel,
  .ant-picker-time-panel {
    background: var(--dk-surface-raised) !important;
    border-color: var(--dk-border-subtle) !important;
    color: var(--dk-text-primary) !important;
  }

  .ant-picker-panel-container {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5) !important;
  }

  .ant-picker-header,
  .ant-picker-footer,
  .ant-picker-panel .ant-picker-footer {
    border-color: var(--dk-border-subtle) !important;
    color: var(--dk-text-primary) !important;
  }

  .ant-picker-header button,
  .ant-picker-header-view button {
    color: var(--dk-text-primary) !important;
  }

  .ant-picker-content th,
  .ant-picker-content td {
    color: var(--dk-text-secondary) !important;
  }

  /* Cells outside the shown month, and disabled dates, keep antd's rgba(0, 0, 0, 0.25) - black on
     a dark panel. Declared before the in-view rule so that one still wins on equal specificity. */
  .ant-picker-cell,
  .ant-picker-cell-disabled {
    color: var(--dk-text-muted) !important;
  }

  .ant-picker-cell-in-view {
    color: var(--dk-text-primary) !important;
  }

  .ant-picker-cell:hover:not(.ant-picker-cell-selected) .ant-picker-cell-inner {
    background: var(--dk-surface-hover) !important;
    color: var(--dk-text-primary) !important;
  }

  /*
    While a range is being picked antd paints a #cbe6ff slab behind the hovered end of it, on the
    cell inner's ::after rather than on the cell's ::before that the rules below cover. The light
    text sat on that slab and the date read as blank. PLA-8844.
  */
  .ant-picker-date-panel
    .ant-picker-cell-in-view.ant-picker-cell-in-range.ant-picker-cell-range-hover-start
    .ant-picker-cell-inner::after,
  .ant-picker-date-panel
    .ant-picker-cell-in-view.ant-picker-cell-in-range.ant-picker-cell-range-hover-end
    .ant-picker-cell-inner::after {
    background: var(--dk-surface-selected) !important;
  }

  /* The dashed edge drawn round the hovered range, antd's #7ec1ff. */
  .ant-picker-cell-range-hover::after,
  .ant-picker-cell-range-hover-start::after,
  .ant-picker-cell-range-hover-end::after,
  .ant-picker-cell-range-hover-edge-start::after,
  .ant-picker-cell-range-hover-edge-end::after {
    border-color: var(--dk-accent-hover) !important;
  }

  .ant-picker-cell-selected .ant-picker-cell-inner,
  .ant-picker-cell-range-start .ant-picker-cell-inner,
  .ant-picker-cell-range-end .ant-picker-cell-inner {
    background: var(--dk-accent) !important;
    color: #ffffff !important;
  }

  .ant-picker-cell-in-range::before,
  .ant-picker-cell-range-hover::before {
    background: var(--dk-surface-hover) !important;
  }

  .ant-picker-today-btn,
  .ant-picker-now-btn {
    color: var(--dk-accent) !important;
  }

  .ant-picker-time-panel-column > li.ant-picker-time-panel-cell .ant-picker-time-panel-cell-inner {
    color: var(--dk-text-primary) !important;
  }

  /* antd hovers a time cell with #f5f5f5 and the light text vanished into it. PLA-8844. */
  .ant-picker-time-panel-column
    > li.ant-picker-time-panel-cell
    .ant-picker-time-panel-cell-inner:hover {
    background: var(--dk-surface-hover) !important;
    color: var(--dk-text-primary) !important;
  }

  .ant-picker-time-panel-column
    > li.ant-picker-time-panel-cell-disabled
    .ant-picker-time-panel-cell-inner {
    color: var(--dk-text-muted) !important;
  }

  /* Selected reads above hover, so it takes the stronger of the two grounds. */
  .ant-picker-time-panel-column
    > li.ant-picker-time-panel-cell-selected
    .ant-picker-time-panel-cell-inner {
    background: var(--dk-surface-selected) !important;
    color: var(--dk-text-primary) !important;
  }

  .ant-picker-range-arrow::after {
    border-color: var(--dk-border-subtle) var(--dk-border-subtle) transparent transparent !important;
  }

  /* ---- overlays ---------------------------------------------------------
     Reachable because ConfigProvider below re-homes every antd popup into this element instead of
     document.body: the row-actions Popover, the pagination page-size Select, tooltips, dropdowns. */
  .ant-popover-inner,
  .ant-popover-inner-content,
  .ant-dropdown-menu,
  .ant-select-dropdown,
  .ant-tooltip-inner {
    background: var(--dk-surface-raised);
    color: var(--dk-text-primary);
  }

  .ant-popover-inner-content li,
  .ant-popover-inner-content ul {
    background: var(--dk-surface-raised) !important;
    color: var(--dk-text-primary);
  }

  .ant-popover-inner-content a {
    color: var(--dk-text-primary);
  }

  .ant-popover-inner-content li:hover {
    background: var(--dk-surface-hover) !important;
  }

  .ant-popover-arrow-content::before,
  .ant-popover-arrow-content {
    background: var(--dk-surface-raised);
  }

  .ant-select-item,
  .ant-dropdown-menu-item,
  .ant-dropdown-menu-submenu-title {
    color: var(--dk-text-primary);
  }

  .ant-dropdown-menu-item:hover,
  .ant-dropdown-menu-item-active {
    background: var(--dk-surface-hover);
  }

  .ant-select-item-option-active:not(.ant-select-item-option-disabled),
  .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background: var(--dk-surface-hover);
    color: var(--dk-text-primary);
  }

  /* Disabled options keep antd's rgba(0, 0, 0, 0.25) and disappear on the dark dropdown. */
  .ant-select-item-option-disabled {
    color: var(--dk-text-muted);
  }

  /* ---- detail-page surfaces ---------------------------------------------
     The customer detail page's panels, audit log and tab bodies take their light appearance from
     antd's default theme rather than from literal colours, so they need rules rather than
     component selectors. All still scoped to this wrapper. */
  .ant-descriptions-item-label {
    color: var(--dk-text-secondary);
  }

  .ant-descriptions-item-content,
  .ant-descriptions-title {
    color: var(--dk-text-primary);
  }

  .ant-descriptions-bordered .ant-descriptions-item-label,
  .ant-descriptions-bordered .ant-descriptions-item-content {
    border-color: var(--dk-border-subtle);
    background: transparent;
  }

  .ant-descriptions-bordered .ant-descriptions-view {
    border-color: var(--dk-border-subtle);
  }

  .ant-collapse,
  .ant-collapse-content,
  .ant-collapse > .ant-collapse-item {
    background: transparent;
    border-color: var(--dk-border-subtle);
    color: var(--dk-text-primary);
  }

  .ant-collapse > .ant-collapse-item > .ant-collapse-header {
    color: var(--dk-text-primary);
  }

  .ant-list,
  .ant-list-item {
    color: var(--dk-text-primary);
    border-color: var(--dk-border-subtle) !important;
  }

  .ant-list-item-meta-description {
    color: var(--dk-text-secondary);
  }

  .ant-timeline,
  .ant-timeline-item-content {
    color: var(--dk-text-primary);
  }

  .ant-timeline-item-tail {
    border-color: var(--dk-border-strong);
  }

  .ant-statistic-title {
    color: var(--dk-text-secondary);
  }

  .ant-statistic-content {
    color: var(--dk-text-primary);
  }

  .ant-steps-item-title,
  .ant-steps-item-description {
    color: var(--dk-text-primary) !important;
  }

  .ant-form-item-label > label {
    color: var(--dk-text-secondary);
  }

  .ant-radio-wrapper,
  .ant-checkbox-wrapper,
  .ant-switch-inner {
    color: var(--dk-text-primary);
  }

  .ant-picker-input > input {
    color: var(--dk-text-primary);
  }

  .ant-picker-suffix,
  .ant-picker-separator {
    color: var(--dk-text-secondary);
  }

  .ant-spin-text {
    color: var(--dk-text-secondary);
  }

  .ant-alert {
    border-color: var(--dk-border-subtle);
  }

  .ant-tabs-content,
  .ant-tabs-tabpane {
    color: var(--dk-text-primary);
  }

  /* ---- misc ------------------------------------------------------------- */
  .ant-divider {
    border-color: var(--dk-border-subtle);
  }

  a {
    color: var(--dk-link);
  }

  a:hover {
    color: var(--dk-link-hover);
  }

  ${({ $variant }) => ($variant === 'orbit-builder' ? orbitBuilderVariant : '')}
`;

/**
 * Styles ONLY modals that opt in with wrapClassName={DARK_MODAL_CLASS}. It is global because antd
 * portals a Modal to document.body, outside any scope element - the class is what keeps it from
 * touching every other modal in the app. PLA-8844.
 */
const DarkModalStyles = createGlobalStyle`
  /*
    Opened under the identity card. The wrap starts at the card's bottom edge instead of filling the
    viewport, and the modal sits at the top of it rather than 100 down. The mask is a SIBLING of the
    wrap and carries no wrapClassName, so the body class the hook sets is what reaches it - without
    that the card would be dimmed by the very scrim meant to leave it readable.

    Both fall back to antd's own 100 when the hook has not measured a card, so a modal opened on a
    page that draws no card still lands somewhere sensible.
  */
  /*
    The body class is only present while a customer identity card is on screen, so this reaches the
    customer detail page and leaves every other dark page's modals centred where antd puts them.
    The mask is offset too - dimming the card would defeat the point of keeping it visible.
  */
  /* The wrap starts where the mask starts. antd leaves it at inset 0, so margin: 0 auto centred
     the dialog on the VIEWPORT while the mask stopped at the sider - below 1489px (1549 with the
     AI WorkSpace flag) the modal's left edge crossed the sider's right edge and sat on nav the
     mask had deliberately left lit. Sharing one edge removes that state at every width instead of
     adding a breakpoint that would only move it. PLA-8844. */
  body.${UNDER_CARD_BODY_CLASS} .${DARK_MODAL_CLASS} {
    top: var(${UNDER_CARD_VAR}, 100px);
    left: var(${UNDER_CARD_LEFT_VAR}, 0px);
    height: auto;
    bottom: 0;
  }

  /* Percent of the WRAP, not of 100vw: the wrap excludes both the sider and the scrollbar antd
     compensates for, so the cap can never exceed the space the modal actually has. */
  body.${UNDER_CARD_BODY_CLASS} .${DARK_MODAL_CLASS} .ant-modal {
    top: 0;
    padding-bottom: 24px;
    max-width: calc(100% - ${SIDE_GUTTER * 2}px);
  }

  /* Left edge as well as top: the backdrop dimmed the sidebar, which is chrome the modal does not
     own and which stays usable behind it. */
  body.${UNDER_CARD_BODY_CLASS} .ant-modal-mask {
    top: var(${UNDER_CARD_VAR}, 0px);
    left: var(${UNDER_CARD_LEFT_VAR}, 0px);
  }

  .${DARK_MODAL_CLASS} {
    ${darkTokenDeclarations}

    /*
      Modal chrome from the reference modal, nodeId 5290:17198 (Loyalty Program Details / Golden
      Points): radius 18, no border, and TWO directional shadows - down-left and right - rather than
      a centred elevation. The design draws no scrim; the mask is kept because it carries the
      modality, but dropped to a light dim so it does not read as a grey sheet.
    */
    .ant-modal-content {
      background: var(--dk-surface-card);
      color: var(--dk-text-primary);
      border: 0;
      border-radius: 18px;
      box-shadow: -6px 7px 4px rgba(0, 0, 0, 0.25), 7px 0 4px rgba(0, 0, 0, 0.25);
    }

    .ant-modal-header,
    .ant-modal-body {
      background: var(--dk-surface-card);
      color: var(--dk-text-primary);
      border-radius: 18px 18px 0 0;
    }

    /*
      The design draws a fixed 923-tall shell with the content scrolling inside it; without a bound
      the body grows to fit and the modal ran 4346px - taller than any screen, so the footer and the
      lower panels were unreachable without scrolling the page itself. Capped against the viewport
      rather than a literal 923 so a short screen still fits.
    */
    .ant-modal-body {
      max-height: calc(100vh - 180px);
      overflow-y: auto;
    }

    /*
      Blocks sit ~10px apart. The frames are absolutely positioned, so this was first read as having
      no rhythm at all - that measurement was top-to-top, which mixes in each block's own height.
      Top-to-previous-bottom is a consistent 10 (9.9-10.05 across seven gaps).
    */
    .ant-modal-body > * + * {
      margin-top: 10px;
    }

    .ant-modal-header {
      border-bottom-color: var(--dk-border-subtle);
    }

    /*
      Panels nested in a modal drop to the RAISED surface with radius 12 - the ladder is page
      0A0F1C, card 101830, modal 101830, panel-in-modal 0D1428, which inverts the usual direction.
    */
    .ant-card,
    .ant-card-body {
      background: var(--dk-surface-raised) !important;
      border-radius: 12px !important;
      border: 0 !important;
      box-shadow: none !important;
    }

    /*
      Accordion, nodeId 5290:17201: the shell is a bordered 44-high box with NO fill and radius 10;
      only its head carries the raised fill, with T13 R16 B13 L16 padding and an 8 gap.
    */
    ${CollapseMultiPanel} > .ant-collapse-item {
      border: 1px solid var(--dk-border-subtle) !important;
      border-radius: 10px !important;
      background: transparent !important;
      margin-bottom: 10px;
      overflow: hidden;
    }

    ${CollapseMultiPanel} > .ant-collapse-item > .ant-collapse-header {
      min-height: 42px;
      padding: 13px 16px !important;
      background: var(--dk-surface-raised) !important;
      color: var(--dk-text-secondary) !important;
      font-size: 13px;
      font-weight: 700;
    }

    ${CollapseMultiPanel} > .ant-collapse-item > .ant-collapse-content {
      background: transparent !important;
      border-top: 1px solid var(--dk-border-subtle) !important;
    }

    /*
      The body, 5290:17212, is layout NONE with zero padding - every child in the file is absolutely
      placed, so there is no flex gap to inherit and nothing holds the first control off the header.
      The drawn rhythm: first label 17 below the body's top border, label to control 6, control to
      the next label 16, a symmetric 16 inset. PLA-8844.
    */
    ${CollapseMultiPanel} > .ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box {
      padding: 17px 16px 16px;
    }

    ${CollapseMultiPanel} .ant-form-item {
      margin-bottom: 16px;
    }

    ${CollapseMultiPanel} .ant-form-item:last-of-type {
      margin-bottom: 15px;
    }

    /*
      The current-tier banner, nodeId 5290:17238. The design fills it opaque #4F46E5; the component
      paints gold or azure from its own palette, which read as a peach slab on a dark modal. This is
      Loyalty-specific - the standalone warning banner is amber at 10%, so the indigo must not be
      generalised to every banner.
    */
    ${UserCurrentTier} {
      background: var(--dk-accent) !important;
      border-left: 0 !important;
      border-radius: 10px;

      .anticon,
      a {
        color: var(--dk-on-accent) !important;
      }

      /*
        The banner's mark is the design's stroked pennant, 5290:17239 - 27 x 40, two white strokes
        at weight 2 - where the component ships a 39 x 33 filled glyph. Painted as a background and
        the shipped paths hidden, rather than swapped in the component, because LineProgressBar is
        the light customer page's too and its icon must not change. PLA-8844.
      */
      svg {
        width: 27px;
        height: 40px;
        background: url("data:image/svg+xml,%3Csvg width='27' height='40' viewBox='0 0 27 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1V38.1409' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M25.3632 20.9989H1V1H25.3632L17.2421 10.9995L25.3632 20.9989Z' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") no-repeat center / contain;
      }

      svg > * {
        display: none;
      }
    }

    /*
      The badge naming the FIRST tier, nodeId 5290:17351: warning amber at 10% over a 35% hairline,
      where every other tier keeps the indigo of 5290:17238. The two nodes are byte-identical apart
      from colour, so only colour is overridden here. Scoped on the attribute rather than by
      relaxing the rule above, so every other badge still resolves to the blanket indigo. The design
      keeps the pennant white in both, so the icon above is inherited. PLA-8844.
    */
    ${UserCurrentTier}[data-variant='amber'] {
      background: rgba(243, 185, 95, 0.1) !important;
      border: 1px solid rgba(243, 185, 95, 0.35) !important;
    }

    ${UserCurrentTier}[data-variant='amber'] ${CurrentTierContent} {
      color: var(--dk-warning) !important;
    }

    /* The dismiss control in the forced-tier banner sits on the accent slab, so it takes the
       banner's ink rather than the component's link blue. */
    ${FooterForced},
    ${DeleteLink} {
      color: var(--dk-on-accent) !important;
    }

    ${CurrentTierContent} {
      color: var(--dk-on-accent) !important;
    }

    /* Tier rail 5290:17258: 5 high, fully rounded, on the border ink; dots ringed in the card fill.
       ProgressFooter is NOT part of the rail - it is the block holding the forced-tier sentence and
       the two points lines - and including it here wrapped all four in a 99-radius capsule filled
       with the rail's ink. That block draws nothing (5290:17268 / 17278 / 17280). PLA-8844. */
    ${ProgressBar} {
      background: var(--dk-border-subtle) !important;
      border-radius: 99px !important;
      height: 5px !important;
    }

    /*
      The FILL, not the track, and it must never share the track's colour: ProgressLine sits at
      z-index 2 over ProgressBar at z-index 1, so painting both the same ink makes the fill
      invisible at every width - which is what a rule grouping the two selectors together did until
      2026-09-22. Keep them apart.
      The design draws no filled rail to copy a colour from: its only "progress reached" signal is
      the active dot 5290:17261 at #268AFF, which is --dk-link, so the fill takes that and agrees
      with the active dot below (TierMarker active uses the same token). PLA-8844.
    */
    ${ProgressLine} {
      background: var(--dk-link) !important;
      border-radius: 99px !important;
      height: 5px !important;
    }

    ${ProgressFooter} {
      background: transparent !important;
      border: 0 !important;
      border-radius: 0 !important;
    }

    /*
      Tier rail, nodeId 5290:17248. Dots are 14 with a 2px ring in the card fill - inactive on the
      secondary ink, active on the link blue. The component paints them from its own light palette
      (#CBCBCB / #e4e4e4), which vanishes on a dark modal.
    */
    ${TierMarker} {
      width: 14px !important;
      height: 14px !important;
      border: 2px solid var(--dk-surface-card) !important;
      background: var(--dk-text-secondary) !important;
      box-shadow: none;
    }

    /* Was [class*='isActive'] / .active, which matched NOTHING: styled-components renders only
       sc-<hash> classes, so every dot took the inactive ink and the design's #268AFF dot
       (5290:17261) never appeared. Driven off data-active now. PLA-8844. */
    ${TierMarker}[data-active='true'] {
      background: var(--dk-link) !important;
    }

    /* Range chips read as bright blue slabs against the rail; the design keeps the chip quiet but
       the figure itself WHITE at 10/400, not the muted ink this rule first gave it (5290:17267). */
    ${RangeContainer} {
      background: var(--dk-surface-raised) !important;
      border: 1px solid var(--dk-border-subtle);
      border-radius: 4px;
      padding: 2px 8px;
      font-size: 10px;
      font-weight: 400;
      color: var(--dk-text-secondary) !important;
    }

    /* 5290:17266: the current tier's chip is the accent with no border and white ink. It reads 16
       tall against the others' 18 purely because they carry a 1px border and it does not - that is
       the design, not a misalignment. */
    ${RangeContainer}[data-active='true'] {
      background: var(--dk-accent) !important;
      border: 0 !important;
      color: var(--dk-on-accent) !important;
    }

    /*
      The emphasised words in "User has been forced to <Tier> Tier status" carry NO pill in the
      section the design links (5290:17279) - they are bold ink on the block's own ground. The
      component's own colorAzure #D6EFF9 panel is a light-palette leftover and goes. PLA-8844.
    */
    ${BlueBgBox} {
      display: inline;
      background: transparent !important;
      border: 0 !important;
      border-radius: 0;
      padding: 0;
      margin: 0 4px;
      font-size: 13px;
      font-weight: 700;
      color: var(--dk-text-primary) !important;
    }

    /*
      The three prose blocks - the forced-tier sentence, "earned N Point(s) during this cycle" and
      "has N Point(s) available" - draw NO fill and NO border of their own (5290:17268 / 17278 /
      17280, all fills: [] and strokes: []). Their type is Inter Regular 13 #AEBBD0, centred.
    */
    ${FooterForced},
    ${FooterForced} > div,
    ${ProgressFooter} p,
    [data-testid='points-earned'] {
      background: transparent !important;
      border: 0 !important;
      font-size: 13px;
      font-weight: 400;
      line-height: 24px;
      color: var(--dk-text-secondary) !important;
      text-align: center;
    }

    ${FooterForced} ${BlueBgBox},
    ${ProgressFooter} p strong {
      font-weight: 700;
    }

    /* The modal's own CLOSE button is a filled accent pill, nodeId 5290:17342. */
    .ant-modal-footer .ant-btn-primary {
      border-radius: 8px;
    }

    /* Save, 5290:17223: the accent slab under the form, full width at 39 high, radius 8, no border.
       Its label is Arial Bold 13 in the file - an untouched component default, so Inter is kept and
       only the size taken. PLA-8844. */
    .ant-collapse-content .ant-btn-primary {
      width: 100%;
      height: 39px;
      border-radius: 8px;
      border: 0;
      font-size: 13px;
      font-weight: 700;
    }

    .ant-modal-title,
    .ant-modal-close {
      color: var(--dk-text-primary);
    }

    .ant-modal-footer {
      border-top-color: var(--dk-border-subtle);
    }

    /*
      Modals that pass a null footer prop draw their own action row with ModalFormFooter, which
      hardcodes a light rgb(227, 227, 227) border-top. Mirror of the .ant-modal-footer rule
      above; scoped to the dark modal so the light theme keeps the grey. PLA-8844.
    */
    ${ModalFormFooter} {
      border-top-color: var(--dk-border-subtle);
    }

    /*
      antd's dragger rules are deeper than a two-class descendant selector
      (.ant-upload.ant-upload-drag p.ant-upload-text), so these match its weight and add !important.
    */
    .ant-upload.ant-upload-drag {
      background: var(--dk-surface-raised) !important;
      border-color: var(--dk-border-strong) !important;
    }

    .ant-upload.ant-upload-drag:not(.ant-upload-disabled):hover {
      border-color: var(--dk-accent) !important;
    }

    .ant-upload.ant-upload-drag p.ant-upload-text {
      color: var(--dk-text-primary) !important;
    }

    .ant-upload.ant-upload-drag p.ant-upload-hint {
      color: var(--dk-text-secondary) !important;
    }

    .ant-upload.ant-upload-drag p.ant-upload-drag-icon .anticon {
      color: var(--dk-accent) !important;
    }

    .ant-upload-list-item-name,
    .ant-upload-list-item .anticon {
      color: var(--dk-text-primary) !important;
    }

    .ant-upload-list-item:hover .ant-upload-list-item-info {
      background: var(--dk-surface-hover) !important;
    }

    /*
      The Loyalty Program Details modal carries a full page inside it: a Change Status / Change Tier
      / Add-Remove-Points tab strip, a table, date pickers and a points input. None of that is
      covered by the modal chrome rules above.
    */
    /*
      WrapperPanel is an antd Card and this modal renders three of them, so without these the tab
      strip, the points form and the period readouts all sit on a white slab inside a dark modal.
    */
    /*
      The Loyalty Program Details modal is built from two shared components that hardcode white, and
      neither is antd, so class-based rules never reached them. Targeted by component selector:
        CollapseMultiPanel.tsx:17  - .ant-collapse-header background: white  (Change Status / Tier)
        LineProgressBar/style.tsx  - TierTitle white chip with near-black ink (Base Tier / issue
                                     rule), the rail, and ProgressHeaderDate for the period text.
    */
    /*
      The accordion GROUP draws no box of its own. Each panel is bordered (5290:17201 / 17206 /
      17224, 1 px #212D4B radius 10) but antd's .ant-collapse carries a border too, and these rules
      only ever recoloured it - so every panel sat inside a second, outer border. PLA-8844.
    */
    ${CollapseMultiPanel} {
      background: transparent !important;
      box-shadow: none !important;
      border: 0 !important;
      border-radius: 0 !important;
    }

    ${CollapseMultiPanel} > .ant-collapse-item > .ant-collapse-header,
    ${CollapseMultiPanel} > .ant-collapse-item.ant-collapse-item-active > .ant-collapse-header {
      background: var(--dk-surface-raised) !important;
      color: var(--dk-text-primary) !important;
      border-color: var(--dk-border-subtle) !important;
    }

    ${CollapseMultiPanel} > .ant-collapse-item {
      border-color: var(--dk-border-subtle) !important;
    }

    /* Tier labels 5290:17251 / 17253 / 17255: Inter 12, lh 15. The CURRENT tier is Bold on the
       primary ink; the others are Medium on the secondary ink. The component paints both at 14 and
       gives them no active/inactive distinction at all. */
    ${TierTitle} {
      background: transparent !important;
      font-size: 12px;
      line-height: 15px;
      font-weight: 500;
      color: var(--dk-text-secondary) !important;
    }

    ${TierTitle}[data-active='true'] {
      font-weight: 700;
      color: var(--dk-text-primary) !important;
    }

    /* The period block is two lines with different type: the date is 12/700 and the timezone and
       caption below it 11/400, both #AEBBD0. Figma 5290:17232-17233 / 5290:17236-17237; the shared
       component draws both at 8px, which is the light page's size. The caption node is one two-line
       text box 33 tall in the file, so the per-line figure is 16.5, not 33. PLA-8844. */
    ${ProgressHeaderDate} {
      font-size: 12px;
      font-weight: 700;
      line-height: 18px;
      color: var(--dk-text-secondary) !important;

      > span {
        font-size: 11px;
        font-weight: 400;
        line-height: 16.5px;
      }
    }

    /* FORCED TIER: 9.5/700 white (5290:17257). The shared component uppercases in CSS and the
       design has it typed in capitals, so both render the same and the rule is left alone.

       The pill is the ACCENT, not the sky blue the component paints: colorBlue is #25A9E5, a
       leftover from the light palette, and it is the one blue that survived the banner rule because
       it lives on a different node. Same for the forced banner's icon below. PLA-8844. */
    ${ForcedLabel} {
      background: var(--dk-accent) !important;
      border-radius: 5px;
      padding: 3px 9px;
      font-size: 9.5px;
      font-weight: 700;
      color: var(--dk-on-accent) !important;
    }

    ${TierMarker} {
      box-shadow: 0 0 0 2px var(--dk-surface-card);
    }

    /* Layout wrappers, not surfaces: the field itself is .ant-input-number and it carries the fill.
       Painting these too stacked a second box behind the control. */
    .ant-input-number-affix-wrapper {
      background: var(--dk-surface-raised) !important;
      border-color: var(--dk-border-subtle) !important;
    }

    .ant-input-number-prefix:empty {
      display: none;
    }

    .ant-card,
    .ant-card-body,
    .ant-card-head {
      background: var(--dk-surface-card) !important;
      color: var(--dk-text-primary);
      border-color: var(--dk-border-subtle) !important;
    }

    .ant-card-head-title,
    .ant-card-extra {
      color: var(--dk-text-primary);
    }

    /* Input addons and the number stepper are separate surfaces from the field itself. */
    .ant-input-group-addon,
    .ant-input-number-group-addon {
      background: var(--dk-surface-hover) !important;
      border-color: var(--dk-border-subtle) !important;
      color: var(--dk-text-secondary) !important;
    }

    .ant-input-number {
      background: var(--dk-surface-raised) !important;
      color: var(--dk-text-primary) !important;
    }

    .ant-input-number-input-wrap,
    .ant-input-number-input {
      color: var(--dk-text-primary) !important;
    }

    .ant-input-number-handler {
      border-color: var(--dk-border-subtle) !important;
    }

    .ant-input-number-handler:hover .ant-input-number-handler-up-inner,
    .ant-input-number-handler:hover .ant-input-number-handler-down-inner {
      color: var(--dk-accent) !important;
    }

    .ant-input-affix-wrapper {
      background: var(--dk-surface-raised) !important;
      border-color: var(--dk-border-subtle) !important;
    }

    .ant-input-affix-wrapper > input.ant-input {
      background: transparent !important;
    }

    /* Period readouts and any plain helper text inside the card. */
    .ant-card-body p,
    .ant-card-body span,
    .ant-card-body div {
      border-color: var(--dk-border-subtle);
    }

    .reward-name {
      color: var(--dk-text-primary) !important;
    }

    .reward-id {
      color: var(--dk-text-secondary) !important;
    }

    .ant-tag[data-state='ended'],
    .ant-tag[data-state='draft'],
    .ant-tag[data-state='processing'] {
      background: var(--dk-surface-hover) !important;
      border: 1px solid var(--dk-border-strong) !important;
      color: var(--dk-text-secondary) !important;
    }

    /*
      The remaining states keep StatusTag's light-mode fill, which antd writes inline as a
      background-color, so only !important reaches it. Drawn here as a tinted chip - dark
      ground, semantic border and text - which keeps success and failure apart without
      putting a saturated slab on the dark table. PLA-8844.
    */
    .ant-tag[data-state='completed'],
    .ant-tag[data-state='complete'],
    .ant-tag[data-state='success'],
    .ant-tag[data-state='processed'],
    .ant-tag[data-state='issued'],
    .ant-tag[data-state='active'] {
      background: var(--dk-tint-positive-bg) !important;
      border: 1px solid var(--dk-success) !important;
      color: var(--dk-success) !important;
    }

    .ant-tag[data-state='failed'],
    .ant-tag[data-state='fail'],
    .ant-tag[data-state='timeout'],
    .ant-tag[data-state='expired'],
    .ant-tag[data-state='inactive'] {
      background: var(--dk-tint-negative-bg) !important;
      border: 1px solid var(--dk-danger) !important;
      color: var(--dk-danger) !important;
    }

    .ant-tag[data-state='pending'],
    .ant-tag[data-state='errors'],
    .ant-tag[data-state='redeemed'] {
      background: var(--dk-surface-hover) !important;
      border: 1px solid var(--dk-warning) !important;
      color: var(--dk-warning) !important;
    }

    .ant-tag[data-state='scheduled'],
    .ant-tag[data-state='approved'],
    .ant-tag[data-state='running'] {
      background: var(--dk-surface-hover) !important;
      border: 1px solid var(--dk-link) !important;
      color: var(--dk-link-hover) !important;
    }

    /* An uncoloured antd Tag is a near-white chip; coloured ones carry their own semantic fill. */
    .ant-tag:not([class*='ant-tag-has-color']) {
      background: var(--dk-surface-hover);
      border-color: var(--dk-border-strong);
      color: var(--dk-text-primary);
    }

    .ant-tabs-tab {
      color: var(--dk-text-secondary);
    }

    .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
      color: var(--dk-text-primary);
    }

    .ant-tabs-ink-bar {
      background: var(--dk-accent);
    }

    .ant-tabs-top > .ant-tabs-nav::before,
    .ant-tabs-nav::before {
      border-bottom-color: var(--dk-border-subtle);
    }

    /*
      Tables inside a modal are denser than the page's: th 30 tall on 8 padding, body rows 52.5 on
      18.5/8/18/8, and the body ink is the secondary rather than the primary. Same chrome-less rule
      as the page - no fills, no zebra, no hover, no selected.
    */
    .ant-table-thead > tr > th {
      height: 30px;
      padding: 8px !important;
      font-size: 10.5px;
      font-weight: 700;
      background: transparent !important;
      border-bottom: 1px solid var(--dk-border-subtle) !important;
      color: var(--dk-text-secondary) !important;
    }

    .ant-table-tbody > tr > td {
      height: 52.5px;
      padding: 18.5px 8px 18px !important;
      font-size: 12px;
      background: transparent !important;
      border-bottom: 1px solid var(--dk-border-subtle) !important;
      color: var(--dk-text-secondary) !important;
    }

    .ant-table-tbody > tr.ant-table-row:hover > td,
    .ant-table-tbody > tr.ant-table-row-selected > td {
      background: transparent !important;
    }

    /* Modal pagination is 28x28 on the CARD surface; the page's is 30x30 on the raised one. */
    .ant-pagination-item,
    .ant-pagination-prev .ant-pagination-item-link,
    .ant-pagination-next .ant-pagination-item-link {
      min-width: 28px;
      height: 28px;
      line-height: 26px;
      background: var(--dk-surface-card) !important;
      border: 1px solid var(--dk-border-subtle) !important;
      border-radius: 6px !important;
    }

    .ant-pagination-item-active {
      border-color: var(--dk-link) !important;
    }

    /* Empty state: "No data", 36 top and bottom. */
    .ant-empty {
      padding: 36px 0;
    }

    .ant-empty-description {
      font-size: 12px;
      color: var(--dk-text-secondary) !important;
    }

    /* Placeholders in a modal are their own ink, neither muted nor secondary. */
    .ant-input::placeholder,
    .ant-input-affix-wrapper input::placeholder,
    .ant-picker-input > input::placeholder,
    .ant-select-selection-placeholder {
      color: var(--dk-text-placeholder) !important;
    }

    /* Modal tab strip: 28 tall, bottom rule, 22 gap, tabs 27 tall with 10 below the label. */
    .ant-tabs-nav {
      height: 28px;
      margin-bottom: 0;
    }

    .ant-tabs-tab {
      height: 27px;
      padding: 0 0 10px;
    }

    .ant-tabs-tab + .ant-tabs-tab {
      margin-left: 22px;
    }

    /* Modal header bar, nodeId 5290:17343: 52 tall, raised fill, r10, inset 8. */
    .ant-modal-header {
      min-height: 52px;
      /* Inset 8 on the left and 5 on the right, with the 5 left as bare modal surface. */
      margin: 8px 5px 8px 8px;
      padding: 9px 16px;
      display: flex;
      align-items: center;
      background: var(--dk-surface-raised) !important;
      border: 1px solid var(--dk-border-subtle) !important;
      border-radius: 10px !important;
    }

    /* In-modal transaction tabs carry the LINK ink, not the white of the card status tabs. */
    .ant-tabs-ink-bar {
      background: var(--dk-link) !important;
    }

    .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
      color: var(--dk-link) !important;
      font-weight: 700;
    }

    /* Field labels, Inter Regular 11.5 #AEBBD0, sitting 6 above their control (5290:17214 /
       5290:17219). antd's own label row leaves no gap at all, so the control met the label. */
    ${FormItem} .ant-form-item-label,
    .ant-form-item-label {
      padding-bottom: 6px !important;
    }

    ${FormItem} .ant-form-item-label > label {
      font-size: 11.5px;
      font-weight: 400;
      height: auto;
      line-height: 14px;
    }

    /*
      Form controls, Figma 5290:17215 (select) and 5290:17220 (date input): fill #0D1428, a 1 px
      #212D4B border and radius 8. Both strokes are strokeAlign INSIDE, so the border eats into the
      box rather than adding to it - without border-box every control comes out 2 px over its drawn
      size. Select 38 high on 10/28/10/16, input 37 on 10/12; the select's wider right gutter is the
      caret's, which the file itself does not draw. PLA-8844.
    */
    .ant-input,
    .ant-input-number,
    .ant-input-affix-wrapper,
    .ant-input-number-affix-wrapper,
    .ant-picker,
    .ant-select-selector {
      box-sizing: border-box;
      border: 1px solid var(--dk-border-subtle) !important;
      border-radius: 8px !important;
      background: var(--dk-surface-raised) !important;
      color: var(--dk-text-primary) !important;
      font-size: 12.5px;
    }

    /*
      ONE element draws the field. antd nests these controls - InputNumber is group-wrapper >
      wrapper > .ant-input-number > .ant-input-number-input, and a search box is
      .ant-input-affix-wrapper > .ant-input - so a field rule that reaches an inner node draws a
      second and third box inside the first. The outer node above owns the border; everything
      within it is bare. PLA-8844.
    */
    .ant-input-affix-wrapper > .ant-input,
    .ant-input-number-affix-wrapper .ant-input-number,
    .ant-input-number-group-wrapper,
    .ant-input-number-wrapper,
    .ant-input-number-input,
    .ant-input-number-input-wrap,
    .ant-picker-input,
    .ant-picker-input > input {
      border: 0 !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
    }

    .ant-select-single .ant-select-selector,
    .ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
      height: 38px !important;
      padding: 0 28px 0 16px !important;
    }

    .ant-select-single .ant-select-selector .ant-select-selection-item,
    .ant-select-single .ant-select-selector .ant-select-selection-placeholder,
    .ant-select-single .ant-select-selector .ant-select-selection-search-input {
      height: 36px !important;
      line-height: 36px !important;
    }

    .ant-picker,
    .ant-input,
    .ant-input-affix-wrapper,
    .ant-input-number-affix-wrapper,
    .ant-input-number {
      min-height: 37px;
      padding: 0 12px;
    }

    /* RHFItemInputNumber passes prefix={<span />}, which makes antd wrap the control in an affix
       wrapper - so the points field is affix-wrapper > .ant-input-number > input, three nested
       nodes. The wrapper is the field; the two inside it are bare, and neither adds height. */
    .ant-input-number-affix-wrapper .ant-input-number,
    .ant-input-number-input {
      min-height: 0 !important;
      height: 35px;
      padding: 0;
    }

    .ant-picker-input > input::placeholder,
    .ant-input::placeholder {
      color: var(--dk-text-placeholder);
    }

    .ant-input-number-handler-wrap {
      background: var(--dk-surface-hover);
      border-left-color: var(--dk-border-subtle);
    }

    .ant-input-number-handler-up-inner,
    .ant-input-number-handler-down-inner,
    .ant-picker-suffix,
    .ant-select-arrow {
      color: var(--dk-text-secondary);
    }

    .ant-picker-input > input {
      color: var(--dk-text-primary);
    }

    /*
      Form labels, e.g. "Override tier expires on:". components/Layout/Form.tsx:299 wraps its rules
      in &&&, so its label colour #000000 outranks a plain label selector three times over - it has
      to be matched by component selector.
    */
    ${FormItem} .ant-form-item-label > label,
    ${FormItem} .ant-form-item-label label {
      color: var(--dk-text-secondary) !important;
    }

    ${FormItem} .ant-form-item-explain,
    ${FormItem} .ant-form-item-extra {
      color: var(--dk-text-muted) !important;
    }

    /* "Override tier expires on:", the period captions and any form label. */
    label,
    .ant-form-item-label > label,
    .ant-descriptions-view,
    .ant-descriptions-view table,
    .ant-descriptions-row,
    .ant-descriptions-row > th,
    .ant-descriptions-row > td,
    .ant-table-bordered .ant-table-cell,
    .ant-table-container {
      border-color: var(--dk-border-subtle) !important;
    }

    .ant-descriptions-item-label {
      color: var(--dk-text-secondary) !important;
    }

    .ant-descriptions-item-content {
      color: var(--dk-text-primary) !important;
    }

    .ant-typography {
      color: var(--dk-text-secondary);
    }

    .ant-table {
      background: transparent;
      color: var(--dk-text-primary);
    }

    .ant-table-thead > tr > th {
      background: var(--dk-surface-raised) !important;
      color: var(--dk-text-secondary) !important;
      border-bottom-color: var(--dk-border-subtle) !important;
    }

    .ant-table-tbody > tr > td {
      border-bottom-color: var(--dk-border-subtle);
      color: var(--dk-text-primary) !important;
    }

    /* StyledTable forces td colour and link colour with !important and a deeper selector, so the
       modal has to answer at the same weight for tables built on it. */
    ${StyledTable}.ant-table-wrapper td,
    ${TableWithNoWrapHeader}.ant-table-wrapper td {
      color: var(--dk-text-primary) !important;
    }

    ${StyledTable}.ant-table-wrapper td a,
    ${TableWithNoWrapHeader}.ant-table-wrapper td a {
      color: var(--dk-link) !important;
    }

    .ant-table-tbody > tr.ant-table-row:hover > td {
      background: var(--dk-surface-hover) !important;
    }

    .ant-empty-image svg {
      opacity: 0.35;
    }

    .ant-empty-description {
      color: var(--dk-text-muted);
    }

    .ant-divider,
    .ant-divider-horizontal {
      border-color: var(--dk-border-subtle);
    }

    .ant-btn-default {
      background: var(--dk-surface-raised);
      border-color: var(--dk-border-strong);
      color: var(--dk-text-primary);
    }

    .ant-btn-primary {
      background: var(--dk-accent);
      border-color: var(--dk-accent);
      color: #ffffff;
    }

    .ant-btn-primary:hover,
    .ant-btn-primary:focus {
      background: var(--dk-accent-hover);
      border-color: var(--dk-accent-hover);
      color: #ffffff;
    }

    /* Disabled Upload sits at antd's #f5f5f5 until a file is picked - a light slab on a dark modal. */
    .ant-btn[disabled],
    .ant-btn[disabled]:hover {
      background: var(--dk-surface-hover) !important;
      border-color: var(--dk-border-subtle) !important;
      color: var(--dk-text-muted) !important;
    }

    /* The sample-file link hardcodes #fb7901 in CustomerUploadModal; overridden, not edited. */
    a {
      color: var(--dk-link) !important;
    }
  }
`;

export interface IDarkPageScopeProps {
  children?: React.ReactNode;
  className?: string;
  /**
   * When false the page renders in the ordinary light theme and nothing here applies - no wrapper,
   * and no modal styles, so a modal carrying DARK_MODAL_CLASS stays light too because the global
   * style that colours that class is only injected while a dark scope is mounted.
   */
  enabled?: boolean;
  /**
   * Overrides the top of the page inset only. Customer 360's Engagement tab passes
   * `C360_LAYOUT.pagePaddingTop` so its header sits on the same line as the other two AI screens';
   * every other dark page leaves this alone and keeps `DARK_SCOPE_TOP_INSET`. PLA-8844.
   */
  topInset?: string;
  variant?: DarkPageScopeVariant;
}

/**
 * Opts ONE page into dark mode. Wrap a page's own content in it; never wrap the app shell, which is
 * a sibling of the route content in IndexPage and must stay on the tenant theme.
 */
const DarkPageScope: React.FC<IDarkPageScopeProps> = ({
  children,
  className,
  enabled = true,
  topInset,
  variant = 'page'
}) => {
  const scopeRef = useRef<HTMLDivElement>(null);

  /* Above the early return: a hook cannot sit behind a condition. It no-ops when the page has no
     identity card, which is how the list and transactions pages keep centred modals. */
  useModalUnderCard(enabled);

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <Scope
      ref={scopeRef}
      className={className}
      data-testid="dark-page-scope"
      $topInset={topInset}
      $variant={variant}
    >
      <DarkModalStyles />
      {/*
        Without this every antd overlay portals into document.body, lands outside this element and
        renders light on a dark page. Re-homing them here is what makes the rules above reach the
        row-actions popover and the pagination page-size dropdown.
      */}
      <ConfigProvider getPopupContainer={() => scopeRef.current ?? document.body}>
        {children}
      </ConfigProvider>
    </Scope>
  );
};

export default DarkPageScope;

export { DARK_MODAL_CLASS };
